import React, { useRef, useState, useEffect, useCallback } from "react";
import styles from "styles.module.css";
//@ts-ignore
import "./style.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addYears, addMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { storageStatisticsGridColumnItems } from "./GridColumns";
import { getStorageStatisticsApi, StorageStatisticFilter } from "api/sales";
import ColumnDropdown from "./utils/dropdown";
import Filter from "./Filter";
import { SalesStatistics } from "interface/sales";
import { useInfoStore } from "store/error";
import { CustomError } from "interface";
import { GridColumnProps } from "@progress/kendo-react-grid";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { aggregatesFields } from "./utils/data";
import { GroupHeaderCellGrossWeight } from "ui/grid/CustomGrid";
import Loading from "ui/Loading";
import StoreStatisticsMobileList from "./StoreStatisticsMobileList";
import { useUserStore } from "store/user";
import { State } from "@progress/kendo-data-query";

const StoreStatisticsPage = () => {
	const { t } = useTranslation();

	const gridRef = useRef<HTMLDivElement>(null);
	// const checkItemsRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [allColumns, setAllColumns] = useState<GridColumnProps[]>([]);
	const columnsRef = useRef<GridColumnProps[]>([]);
	const baseColumnsRef = useRef<GridColumnProps[]>([]); // source-of-truth, never mutated by resize
	const [showPopup, setShowPopup] = useState<boolean>(false);
	const [refReady, setRefReady] = useState<boolean>(false);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [groupCount, setGroupCount] = useState<number>(0);
	const headerRef = useRef<HTMLImageElement | null>(null);
	const [data, setData] = useState<SalesStatistics[]>([]);
	const filteredDataRef = useRef<SalesStatistics[]>([]);
	const [isLoading, setLoading] = useState<boolean>(false);

	const { setError } = useInfoStore();
	const { isAdmin } = useUserStore();

	const [filterOptions, setFilterOptions] = useState<StorageStatisticFilter>({
		fromDate: isAdmin ? addMonths(new Date(), -2) : addYears(new Date(), -1),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	useEffect(() => {
		const updateMobileView = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		updateMobileView();
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (headerRef.current) {
			setRefReady(true);
		}
	}, [headerRef.current]);

	useEffect(() => {
		const newColumns = storageStatisticsGridColumnItems({
			ref: headerRef,
			data: data,
			filteredDataRef: filteredDataRef,
		});
		baseColumnsRef.current = newColumns.map((col) => ({ ...col }));
		columnsRef.current = newColumns;
		setAllColumns(newColumns);
	}, [refReady, data]);

	useEffect(() => {
		columnsRef.current = allColumns;
	}, [allColumns]);

	useEffect(() => {
		setLoading(true);
		getStorageStatisticsApi(filterOptions)
			.then((response) => {
				setData(response.data);
				filteredDataRef.current = response.data;
				setForceUpdate((prev) => !prev);
			})
			.catch((error) => {
				if (error.response?.data) {
					setError(new CustomError("Something went wrong", "error"));
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [filterOptions, setError]);

	const handleStateChange = useCallback((state: State) => {
		const newCount = state.group?.length ?? 0;
		setGroupCount(newCount);
	}, []);

	useEffect(() => {
		if (isMobileView) return;

		const updateColumnWidths = () => {
			// Always start from the original baseline � never from previously adjusted widths
			const baseColumns = baseColumnsRef.current;
			if (baseColumns.length === 0) return;

			const grid = document.querySelector(".k-grid") as HTMLElement;
			const table = document.querySelector(".k-table") as HTMLElement;
			if (!grid || !table) return;

			// Temporarily apply base widths to measure what the table WOULD be
			const productWidthDefault = 250;

			// Calculate total base width of visible columns
			const visibleBaseWidth = baseColumns
				.filter((col) => !col.hidden)
				.reduce((sum, col) => sum + parseWidth(col.width), 0);

			const gridWidth = grid.offsetWidth;
			if (!gridWidth || !visibleBaseWidth) return;

			const addingWidth = gridWidth - visibleBaseWidth;

			if (Math.abs(addingWidth) <= 10) {
				// Close enough � just apply base widths as-is
				columnsRef.current = baseColumns.map((col) => ({ ...col }));
				setAllColumns(baseColumns.map((col) => ({ ...col })));
				return;
			}

			const isProductDescriptionColumnHidden = baseColumns.find(
				(col) => col.field === "productDescription"
			)?.hidden;

			const updatedColumns = baseColumns.map((column) => {
				const newColumn = { ...column };

				if (isProductDescriptionColumnHidden) {
					// No flexible column � skip adjustment
				} else {
					if (newColumn.field === "productDescription") {
						let width = parseWidth(newColumn.width);
						const newWidth = width + addingWidth;
						newColumn.width = Math.max(newWidth, productWidthDefault);
					}
				}
				return newColumn;
			});

			columnsRef.current = updatedColumns;
			setAllColumns(updatedColumns);
		};

		let debounceTimer: ReturnType<typeof setTimeout>;

		const handleResize = () => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(updateColumnWidths, 120);
		};

		const initialTimer = setTimeout(updateColumnWidths, 120);

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
			clearTimeout(debounceTimer);
			clearTimeout(initialTimer);
		};
	}, [data, forceUpdate, isMobileView, groupCount]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	// Keep base columns in sync when user toggles column visibility
	const handleCheckboxChange = (field: string, checked: boolean) => {
		baseColumnsRef.current = baseColumnsRef.current.map((item) =>
			item.field === field ? { ...item, hidden: checked } : item
		);
		setAllColumns((prevItems) =>
			prevItems.map((item) =>
				item.field === field ? { ...item, hidden: checked } : item
			)
		);
		setForceUpdate((prev) => !prev);
	};

	const closeColumnDropdown = () => setShowPopup(false);

	if (!isMobileView && isLoading) {
		return (
			<div className={styles.PageContainer}>
				<Loading show={isLoading} />
			</div>
		);
	}
	return (
		<div className={styles.PageContainer} style={{ height: "auto", minHeight: "calc(100% - 90px)" }}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("StoreStatistics")}
				</legend>
			</div>

			<div ref={checkItemsRef}>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<StoreStatisticsMobileList items={data} loading={isLoading} />
				) : (
					<GridWithGroupPaging
						gridData={data}
						gridColumns={allColumns}
						itemAboveHeight={70}
						dataKey="contractNoteNumber"
						className="sales-statistics-grid"
						exportGridAsExcelRef={excelRef}
						groupHeaderCell={GroupHeaderCellGrossWeight}
						groupable={{ footer: "visible", enabled: true }}
						aggregateFields={aggregatesFields}
						pageable={false}
						sortable={true}
						loading={isLoading}
						excelProps={{
							fileName: `Lagerliste_${new Date().toISOString()}.xlsx`,
						}}
						handleStateChange={handleStateChange}
						onFilteredDataChange={(filtered) => {
							filteredDataRef.current = filtered;
						}}
						gridStyle={{ height: "auto", maxHeight: "none", overflowY: "visible" }}
					/>
				)}
			</div>

			{!isMobileView && refReady && headerRef.current && (
				<ColumnDropdown
					show={showPopup}
					onClose={closeColumnDropdown}
					items={allColumns}
					onCheckboxChange={handleCheckboxChange}
					anchor={headerRef}
					forceUpdate={forceUpdate}
				/>
			)}
		</div>
	);
};

export default StoreStatisticsPage;

const parseWidth = (width: string | number | undefined): number => {
	if (width === undefined) return 0;

	if (typeof width === "number") {
		return width;
	}

	if (typeof width === "string") {
		return parseInt(width.replace("px", ""), 10) || 0;
	}

	return 0;
};
